/**
 * Видео-ролик из платья: типы ответов `/api/video/*`, `/api/projects/{id}/video|scenario|clone`,
 * `/api/clips/*`, `/api/cuts/*` и хуки над ними (спека §9.1).
 *
 * Типы списаны с Python, который эти ответы собирает (`reels/web/api_video.py`,
 * `videoscenario.video_view`/`timeline`/`_view`, `videocut._view`/`compare`, `videoclip.estimate_run`/
 * `enqueue_run`), а не со спеки: где поле приходит `null`, оно `null` и здесь.
 *
 * ДЕНЬГИ: `scenario/compose`, `video/run`, `clips/{gid}/retry`, `cuts/{id}/final-hd` — платные. Ключ идемпотентности у них
 * живёт до УСПЕШНОГО ответа (как `useGenerate` в `lib/project.ts`): двойной клик и повтор после
 * обрыва уходят с тем же ключом, и сервер не платит второй раз. Новый ключ — только после успеха.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { apiFetch, ApiFailure, type ApiErrorItem } from "@/lib/api";
import { errorText, newKey, postJson, qk, type FieldResult, type LibraryRow, type ResolvedSlot } from "@/lib/project";
import type { EditEstimate } from "@/lib/results";
import { secNum, type ScenarioDraftV2 } from "@/lib/videoTimeline";

// ---------------------------------------------------------------------------
// справочник: модели, баланс, ffmpeg — GET /api/video/options
// ---------------------------------------------------------------------------

export type Tier = "cheap" | "value" | "mid" | "premium";

/** Политика лиц модели (`videogen_faces`): выучивается по ответам провайдера. */
export type Faces = "unknown" | "accepts" | "rejects";

export type VideoModel = {
  code: string;
  label: string;
  /** `null` — модель без уровня: доступна в диалоге, в первый прогон не идёт. */
  tier: Tier | null;
  tier_label: string | null;
  /** Галочка в диалоге запуска по умолчанию (v2 §1.1: только Kling 3.0 Pro). */
  default_on: boolean;
  /** Запасная к какой модели (код) — запас уровня в диалоге. */
  fallback_for: string | null;
  /** Модель для кадров с лицом, если эта их отклоняет (`FACE_FALLBACK`, v2 §1.2). */
  face_fallback: string | null;
  /** Чем модель задаёт кадр: размером `WxH` или разрешением `720p`. */
  send: "size" | "resolution";
  default_size: string | null;
  /** Только из discovery; у неоткрытой модели пусто. */
  sizes_9_16: string[];
  resolutions: string[];
  durations: number[];
  frame_images: string[];
  audio: boolean;
  refs: boolean;
  /** Имя параметра негатива у провайдера; `null` — негатив не передаётся. */
  negative: string | null;
  passthrough: Record<string, unknown>;
  faces: Faces;
  discovered: boolean;
  /** Ролик 15 с = 3 кадра по 5 с; `null` — цены нет (причина в `price_error`). */
  price_15s: number | null;
  price_basis: string | null;
  price_error: string | null;
};

/** Баланс OpenRouter; `null` — не узнали (сеть, ключ). */
export type Balance = { left: number; at: string } | null;

export type VideoOptions = {
  models: VideoModel[];
  default_library_id: number;
  libraries: LibraryRow[];
  balance: Balance;
  ffmpeg: { ok: boolean; version: string | null; error: string | null };
  limits: { max_clips: number; parallel: number };
};

// ---------------------------------------------------------------------------
// сценарий
// ---------------------------------------------------------------------------

/** `close`/`motion` — ракурсы видео-ролика из готового кадра (§7.1), остальные — композер сценария. */
export type Framing = "medium" | "wide" | "back" | "detail" | "close" | "motion";
export type ShotTransition = "cut" | "crossfade";

/** Кадр сценария (`validate_scenario`, схема §5.2): RU — заголовок/действие/акцент, EN — промпты. */
export type ScenarioShot = {
  title: string;
  duration_s: number;
  framing: Framing;
  camera: string;
  action: string;
  dress_focus: string;
  keyframe_prompt: string;
  video_prompt: string;
  transition: ShotTransition;
  /** v2: лицо героини в кадре; у версий v1 поля нет. */
  face_visible?: boolean;
};

/**
 * Версия сценария. v2 — `dress_details`, `face_visible` у кадров. Титра и звука у новых версий нет:
 * `title_*`/`sound_mood` — только у старых (у v1 титр — `title_text`), остаются необязательными.
 */
export type ScenarioJson = {
  logline: string;
  hook: string;
  title_text?: string;
  title_name?: string;
  title_cta?: string;
  /** EN, 3–6 ключевых деталей платья. */
  dress_details?: string[];
  sound_mood?: string;
  negative: string;
  heroine: string;
  light: string;
  shots: ScenarioShot[];
  /** Что сервер дописал за композера (инвариант, героиня, хвост негатива) — показывать жёлтым. */
  _fixes: string[];
};

export type ScenarioOrigin = "composer" | "manual";

/** Версия сценария (`videoscenario._view`). */
export type ScenarioVersion = {
  id: number;
  project_id: number;
  version: number;
  origin: ScenarioOrigin;
  author_user_id: number;
  author_name: string;
  model: string | null;
  brief: string | null;
  ai_call_id: number | null;
  cost: number | null;
  date_create: string;
  date_apply: string | null;
  applied: boolean;
  json: ScenarioJson;
};

export type AppliedScenario = { id: number; version: number; origin: ScenarioOrigin; json: ScenarioJson };

/** GET /api/projects/{id}/scenario — версии новые первыми. */
export type ScenarioList = { applied_id: number | null; versions: ScenarioVersion[] };

export type ApplyMode = "shots" | "fields";

export type ApplyResult = {
  video: VideoView;
  applied: {
    scenario_id: number;
    mode: ApplyMode;
    shot_ids: number[];
    updated: number;
    created: number;
    deleted: number;
  };
};

// ---------------------------------------------------------------------------
// ракурсы и стилл кадра (видео-ролик из готового кадра, §7/§8/§10)
// ---------------------------------------------------------------------------

/** `angles` не задан — все коды, у чьего кадра в проекте ещё нет стилла (`generate._frame_view_codes`). */
export type ViewsBody = { angles?: string[]; model?: string; n?: number };

/**
 * Запуск пакета. `replace` — перегенерация ракурса (задача Оли №313): новый кадр займёт место
 * стилла, когда будет готов; `views/estimate` этого поля не знает и знать не должен — цена та же.
 */
export type ViewsRunBody = ViewsBody & { replace?: boolean };

/** Пункт оценки пакета ракурсов (`generate.views_estimate`): `angle` — код ракурса, остальное — как у `editEstimate`. */
export type ViewsEstimateItem = EditEstimate & { angle: string };

export type ViewsEstimateResult = {
  items: ViewsEstimateItem[];
  total: number;
  images: number;
  /** `null` — пустой набор (все ракурсы уже со стиллом); `mixed` — у ракурсов разные основания цены. */
  basis: "facts" | "formula" | "table" | "mixed" | null;
};

export type ViewsRunItem = { angle: string; shot_id: number; generation_id: number };
export type ViewsRunResult = { items: ViewsRunItem[] };

/** `variant_id: null` снимает ссылку — кадр возвращается к стиллу по `picked`-варианту его генераций. */
export type StillBody = { variant_id: number | null };
export type StillResult = { shot_still_variant_id: number | null };

/** `written` — сервис дописал текст в пустые поля кадра; занятые не трогает (текст возвращает всегда). */
export type ClipPromptResult = { video_prompt: string; camera: string; cost: number; written: boolean };

// ---------------------------------------------------------------------------
// хронология и клипы
// ---------------------------------------------------------------------------

export type ClipStatus = "queued" | "running" | "done" | "failed" | "cancelled";
export type ShotFace = "visible" | "hidden";

/** Последний клип модели на кадре (`videoscenario._clips`). */
export type TimelineClip = {
  model: string;
  generation_id: number;
  status: ClipStatus;
  variant_id: number | null;
  asset_path: string | null;
  /** Кадр 0 с готового клипа. */
  poster_path: string | null;
  cost_actual: number | null;
  cost_estimate: number | null;
  error: string | null;
  /** Длительность файла клипа. */
  duration_ms: number | null;
  /** Промпт, которым клип снят (№337): поля кадра могли поменяться, здесь — факт запуска. */
  prompt: string | null;
  /**
   * Перегенерация поверх ГОТОВОГО клипа (№337): сам клип в полях выше — прежний, оплаченный, а это
   * статус новой попытки. `null` — попытки нет (или она и есть сам клип).
   */
  pending: { generation_id: number; status: ClipStatus; error: string | null } | null;
};

export type TimelineShot = {
  shot_id: number;
  order: number;
  title: string;
  duration_ms: number | null;
  camera: string | null;
  video_prompt: string | null;
  /** Переход ПОСЛЕ кадра; `null` — как у монтажа. */
  transition: ShotTransition | null;
  /** Лицо в кадре (`shot_face`; пусто у сервера — `visible`). */
  face: ShotFace;
  /** План кадра (`shot_framing`); `null` — не задан (композер его не всегда пишет; у исходного кадра
   *  проекта «из кадра» — пока сотрудник не сказал, какой это ракурс, см. `source`). */
  framing: Framing | null;
  /** Кадр — исходная генерация проекта «из кадра» (`videoscenario._source_shot`). Его ракурс выбирают
   *  списком в панели «Ракурсы», а не генерируют (задача Оли №305). */
  source: boolean;
  /** Задание ракурса, вписанное сотрудником (`shot_prompt`); `null` — работает каталожное. */
  view_task: string | null;
  /** Каталожное задание ракурса (`generate.VIEW_ANGLES[...].note`); `null` — кадр не ракурс. */
  view_task_default: string | null;
  /** Стилл со звездой — первый кадр клипа; `null` — снимать нечего. */
  still: { variant_id: number; asset_path: string } | null;
  /** В порядке allowlist моделей. */
  clips: TimelineClip[];
};

// ---------------------------------------------------------------------------
// монтажи и сравнение
// ---------------------------------------------------------------------------

export type CutStatus = "queued" | "running" | "done" | "failed";
export type CutTransition = "cut" | "crossfade" | "shots";
export type PickStatus = "none" | "picked" | "rejected";

/** Клип в монтаже (`videocut._view`): сохранённый набор + файл и кадры-миниатюры клипа. */
export type CutItem = {
  shot_id: number;
  variant_id: number;
  /** Сколько клипа идёт в ролик: min(длительность кадра, длительность файла). */
  duration_ms: number;
  transition: ShotTransition;
  generation_id: number | null;
  model: string | null;
  /** Номер кадра клипа. */
  shot_order: number | null;
  /** Смешанный монтаж: клип запасной модели вместо этой (кадр с лицом). */
  routed_from?: string;
  /** Обрезка начала куска, мс (§9.1); нет ключа или 0 — без обрезки. */
  trim_start_ms?: number;
  asset_path: string | null;
  poster_path: string | null;
  /** Кадры-миниатюры по мс (ключ JSON — строка): «0», «1000», «2500», «5000». */
  frames: Record<string, string>;
};

export type Cut = {
  id: number;
  project_id: number;
  /** `null` — смешанный монтаж (клипы разных моделей). */
  model: string | null;
  /** «Seedance 2.0 + Kling 3.0 Pro (кадры 1–2)» — модель и чужие клипы (`videocut.label`). */
  label: string;
  status: CutStatus;
  origin: "auto" | "manual";
  transition: CutTransition;
  items: CutItem[];
  title: string | null;
  title_ms: number;
  music_asset_id: number | null;
  music_path: string | null;
  /** `milla` — грейд «Milla Nova»; `null` — без грейда (и все монтажи v1). */
  grade: string | null;
  logo_asset_id: number | null;
  logo_path: string | null;
  /** Слайд-концовка (§9.2); `outro_ms` не задан — слайда нет. */
  outro_ms: number | null;
  /** Надпись слайда; `null` при заданном знаке (слайд — PNG знака, не текст) или без слайда. */
  outro_text: string | null;
  asset_id: number | null;
  asset_path: string | null;
  poster_path: string | null;
  duration_ms: number | null;
  /** Σ факт клипов; `null` — факт есть не у всех. */
  cost: number | null;
  pick: PickStatus;
  error: string | null;
  author_name: string;
  date_create: string;
  date_start: string | null;
  date_finish: string | null;
  render_seconds: number | null;
  /** Монтаж уже взят в «Результаты» (`adopt`, карусель и видео в публикации, №259) — этот вариант;
   * `null` — ещё не взят. */
  variant_id: number | null;
};

/** Колонка «Сравнения» (§8, `videocut.compare`): последний готовый монтаж модели. */
export type CompareColumn = {
  model: string | null;
  label: string;
  tier: Tier | null;
  tier_label: string | null;
  cut: Cut;
  cost: number;
  cost_is_fact: boolean;
  render_seconds: number | null;
  clip_seconds: number;
};

// ---------------------------------------------------------------------------
// вид проекта и опрос
// ---------------------------------------------------------------------------

export type DressPhoto = { id: number; view_path: string | null; thumb_path: string | null };

/** Платье проекта; папку могли удалить (`deleted`) — готовые клипы остаются. */
export type VideoDress = {
  id: number | null;
  name: string | null;
  note: string | null;
  deleted: boolean;
  photos: DressPhoto[];
};

/** Исходный кадр проекта «из кадра» (`videoscenario._source_variant_view`); `null` — ссылки нет или
 *  исходный вариант удалён (`ON DELETE SET NULL`) — проект остаётся, просто без исходника. */
export type SourceVariant = { variant_id: number; asset_path: string } | null;

/** `video` в GET /api/projects/{id} (`videoscenario.video_view`); у обычного проекта — `null`. */
export type VideoView = {
  /** `frame` — проект из готового кадра (видео-ролик из готового кадра, §6/§10); `dress` — из платья. */
  origin: "frame" | "dress";
  source_variant: SourceVariant;
  /** Проект по видео-референсу (`videoscenario._ref_view`); `null` — обычный. */
  video_ref: { id: number; title: string | null; platform: string | null; marked: number } | null;
  dress: VideoDress;
  library: { id: number; name: string } | null;
  duration_ms: number;
  /** Потолок трат проекта, $ (`project_cost_cap`); `null` — без потолка. */
  cost_cap: number | null;
  /** Сколько осталось до потолка (`generate._cap_left`); `null` — без потолка. */
  cap_left: number | null;
  scenario: { applied: AppliedScenario | null; versions: ScenarioVersion[] };
  timeline: TimelineShot[];
  cuts: Cut[];
  pending_clips: number;
};

/** GET /api/projects/{id}/video — опрос вкладки «Видео». */
export type VideoPoll = {
  timeline: TimelineShot[];
  cuts: Cut[];
  compare: CompareColumn[];
  pending_clips: number;
  balance: Balance;
};

// ---------------------------------------------------------------------------
// тела и ответы записи
// ---------------------------------------------------------------------------

export type CreateVideoBody = { name: string; dress_id: number; library_id: number | null; duration_ms: number };

/** `cost_cap: null` — снять потолок; поля нет — не трогать. */
export type SettingsBody = { dress_id?: number; library_id?: number; duration_ms?: number; cost_cap?: number | null };

/** Сцена в `scene.main` — ровно одно из трёх. */
export type FillBody = {
  dress_id?: number;
  scene_variant_id?: number;
  scene_asset_id?: number;
  reference_item_id?: number;
};

export type FillResult = {
  slots: { shot_id: number; order: number; slots: ResolvedSlot[] }[];
  project_slots: { slot_code: string; asset_id: number; asset_path: string }[];
  loose: number;
};

export type ComposeBody = { brief: string; models: string[]; ref_ids: number[] };
export type ComposeResult = { scenario: ScenarioVersion; cost: number; existing: boolean };

/**
 * Набор запуска: модели, кадры (нет — все), размер по модели, бесшовно, референсы платья. Срез
 * (`crops`) панель не шлёт: у сервера он 0, и кадры с лицом модели `rejects` уходят на запасную.
 */
export type RunParams = {
  models: string[];
  shot_ids?: number[];
  /** `code → size` (или resolution — по `send` модели). */
  sizes?: Record<string, string>;
  seamless: boolean;
  with_refs: boolean;
};

/** Пункт оценки: `model` — фактическая модель клипа, `routed_from` — выбранная, если кадр с лицом ушёл на запасную. */
export type EstimateItem = {
  shot_id: number;
  model: string;
  routed_from: string | null;
  /** Клип уже есть (у кадра или в этом запуске) — цена 0. */
  reused: boolean;
  generation_id?: number | null;
  duration_s: number;
  cost: number;
  basis: string;
  warnings: string[];
};

export type EstimateResult = {
  items: EstimateItem[];
  total: number;
  /** `null` — у проекта нет потолка. */
  cap_left: number | null;
  balance: Balance;
  /** Кадр без стилла/длительности/видео-промпта, лимит клипов, потолок — строками. */
  blockers: string[];
  /** Оценка выше баланса — предупреждение, не блок. */
  balance_warning: string | null;
};

export type RunItem = {
  shot_id: number;
  model: string;
  routed_from: string | null;
  /** Клип запасной модели уже был (или уже ставится в этом запуске) — без новой строки и денег. */
  reused: boolean;
  generation_id: number;
  /** Строка уже была (тот же ключ запуска или двойной клик) — новых денег нет. */
  existing: boolean;
  cost: number;
  basis: string;
};

export type RunResult = {
  items: RunItem[];
  /** Предупреждения монтажа (смешанный: «кадры 1–2 (запасная модель …) останутся 720p»). */
  warnings?: string[];
  total: number;
  cap_left: number | null;
  balance: Balance;
  balance_warning: string | null;
};

export type CutBody = {
  /** Модель — последний готовый клип модели у каждого кадра; или `items` — ручной набор. */
  model?: string;
  /** Ручной набор, по порядку (§9.1): `trim_start_ms` — обрезка начала куска, нет ключа или 0 — без неё. */
  items?: { shot_id: number; variant_id: number; trim_start_ms?: number }[];
  transition: CutTransition;
  /** Не задан или `""` — без титра; две строки — через `\n`. */
  title?: string;
  title_ms: number;
  music_asset_id?: number;
  /** `milla` — грейд «Milla Nova» (по умолчанию у сервера); `null` — без грейда. */
  grade: string | null;
  logo_asset_id?: number;
  /** Слайд-концовка (§9.2): нет поля — без слайда; `outro_text` без знака — надпись (по умолчанию «UNIRENTER»). */
  outro_ms?: number;
  outro_text?: string;
  force?: boolean;
};

export type CutResult = { cut: Cut; existing: boolean };

/** «Финал в 1080p» (v2 §1.4): клипы монтажа со звездой заново в наибольшем 9:16 модели. */
export type FinalHdItem = {
  shot_id: number;
  model: string;
  size: string | null;
  resolution: string | null;
  duration_s: number;
  cost: number;
  basis: string;
  warnings: string[];
  generation_id?: number;
  existing?: boolean;
};

export type FinalHdResult = {
  cut_id: number;
  items: FinalHdItem[];
  /** Клипы, у модели которых формата больше нет. */
  skipped: { shot_id: number; model: string; reason: string }[];
  /** Предупреждения монтажа (смешанный: «кадры 1–2 (запасная модель …) останутся 720p»). */
  warnings?: string[];
  total: number;
  cap_left: number | null;
  balance: Balance;
  balance_warning: string | null;
};

export type ShotVideoField = "shot_duration_ms" | "shot_camera" | "shot_video_prompt" | "shot_transition" | "shot_face";

// ---------------------------------------------------------------------------
// подписи
// ---------------------------------------------------------------------------

export const CLIP_STATUS_LABEL: Record<ClipStatus, string> = {
  queued: "очередь",
  running: "отправлен",
  done: "готово",
  failed: "ошибка",
  cancelled: "отменён",
};

export const FRAMING_LABEL: Record<Framing, string> = {
  medium: "средний",
  wide: "общий",
  back: "со спины",
  detail: "деталь",
  close: "крупный",
  motion: "движение",
};

export const SHOT_TRANSITION_LABEL: Record<ShotTransition, string> = {
  cut: "встык",
  crossfade: "наплыв",
};

/** Подпись клипа на точке/плашке: «модель · статус · $цена» (факт, иначе ≈ оценка). */
export function clipLine(clip: TimelineClip, label: string): string {
  const price =
    clip.cost_actual !== null
      ? `$${clip.cost_actual.toFixed(3)}`
      : clip.cost_estimate !== null
        ? `≈ $${clip.cost_estimate.toFixed(3)}`
        : "цены нет";
  return `${label} · ${CLIP_STATUS_LABEL[clip.status]} · ${price}`;
}

export const CUT_STATUS_LABEL: Record<CutStatus, string> = {
  queued: "в очереди",
  running: "собирается",
  done: "готово",
  failed: "ошибка",
};

export const CUT_TRANSITION_LABEL: Record<CutTransition, string> = {
  cut: "встык",
  crossfade: "наплыв 0,3 с",
  shots: "как в кадрах",
};

/** Миллисекунды → «14,5 с» (обычным шрифтом; в моноширинном `.tnum` — `<Sec>`, единица вне числа). */
export function seconds(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  return `${secNum(ms)} с`;
}

export const GRADE_MILLA = "milla";

/** Ссылка на копию проекта из 409 `SHOTS_HAVE_GENERATIONS` (поле ошибки `clone`); иначе `null`. */
export function cloneLink(error: unknown): string | null {
  if (!(error instanceof ApiFailure) || error.code !== "SHOTS_HAVE_GENERATIONS") return null;
  const item = error.items[0] as (ApiErrorItem & { clone?: string }) | undefined;
  return item?.clone ?? null;
}

// ---------------------------------------------------------------------------
// ключи кэша и чтение
// ---------------------------------------------------------------------------

export const videoKeys = {
  options: ["video-options"] as const,
  poll: (pid: number) => ["video", pid] as const,
  scenarios: (pid: number) => ["video-scenarios", pid] as const,
  estimate: (pid: number, body: string) => ["video-estimate", pid, body] as const,
  finalHd: (cutId: number) => ["final-hd-estimate", cutId] as const,
};

/**
 * Всё, что показывает ролик: карточка проекта (`video`), опрос, версии сценария. После платной
 * записи (`paid`) — ещё и справочник: в нём баланс OpenRouter, иначе он минуту показывал бы старый.
 */
function refresh(client: QueryClient, pid: number, paid = false): void {
  void client.invalidateQueries({ queryKey: qk.project(pid) });
  void client.invalidateQueries({ queryKey: videoKeys.poll(pid) });
  void client.invalidateQueries({ queryKey: videoKeys.scenarios(pid) });
  if (paid) void client.invalidateQueries({ queryKey: videoKeys.options });
}

/** Модели, баланс, ffmpeg. Discovery и баланс меняются редко — минута свежести. */
export function useVideoOptions() {
  return useQuery({
    queryKey: videoKeys.options,
    queryFn: () => apiFetch<VideoOptions>("/api/video/options"),
    staleTime: 60_000,
  });
}

/** Клипы в очереди/работе + монтажи, что ещё собираются: пока > 0 — опрос идёт. */
const inWork = (poll: VideoPoll | undefined): number =>
  (poll?.pending_clips ?? 0) + (poll?.cuts ?? []).filter((c) => c.status === "queued" || c.status === "running").length;

/**
 * Хронология с клипами, монтажи, сравнение, баланс. Опрос — УСЛОВНЫЙ: раз в 5 с, пока есть клипы в
 * очереди/работе или монтаж собирается; готовая хронология не опрашивается. Когда клипы или монтаж
 * доходят до конца, карточка проекта (`video` в ней) перечитывается один раз.
 */
export function useProjectVideo(pid: number, enabled = true) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: videoKeys.poll(pid),
    queryFn: () => apiFetch<VideoPoll>(`/api/projects/${pid}/video`),
    enabled: enabled && Number.isFinite(pid),
    refetchInterval: (q) => (inWork(q.state.data) > 0 ? 5_000 : false),
  });
  const working = inWork(query.data);
  const before = useRef(working);
  useEffect(() => {
    if (working < before.current) void client.invalidateQueries({ queryKey: qk.project(pid) });
    before.current = working;
  }, [working, pid, client]);
  return query;
}

/** Версии сценария (новые первыми) и id применённой. */
export function useScenarios(pid: number, enabled = true) {
  return useQuery({
    queryKey: videoKeys.scenarios(pid),
    queryFn: () => apiFetch<ScenarioList>(`/api/projects/${pid}/scenario`),
    enabled: enabled && Number.isFinite(pid),
  });
}

/** Значение, устоявшееся 300 мс: оценка не дёргает сервер на каждый щелчок чекбокса. */
function useSettled(value: string): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), 300);
    return () => clearTimeout(t);
  }, [value]);
  return settled;
}

/**
 * Оценка запуска (бесплатно, без записи) с задержкой 300 мс после последнего изменения набора.
 * `params = null` или без моделей — не спрашиваем. `fresh` — ответ относится к текущему набору (пока
 * набор не устоялся, старую цену показывать как действующую нельзя).
 */
export function useEstimate(pid: number, params: RunParams | null) {
  const body = params && params.models.length > 0 ? JSON.stringify(params) : "";
  const settled = useSettled(body);
  const query = useQuery({
    queryKey: videoKeys.estimate(pid, settled),
    queryFn: () => postJson<EstimateResult>(`/api/projects/${pid}/video/estimate`, JSON.parse(settled) as RunParams),
    enabled: settled !== "" && settled === body,
    retry: false,
  });
  return { ...query, fresh: body !== "" && settled === body };
}

// ---------------------------------------------------------------------------
// платные: ключ до успешного ответа
// ---------------------------------------------------------------------------

/**
 * Платная запись. Ключ — на «область» (`scope`: весь проект или один клип): пока ответа нет,
 * повтор в той же области уходит с тем же ключом, а второй клик до перерисовки не шлёт POST вовсе.
 * Новый ключ области — только после успешного ответа.
 */
function usePaid<V, R>(
  send: (vars: V, key: string) => Promise<R>,
  scopeOf: (vars: V) => string,
  onDone: (result: R, vars: V) => void,
) {
  const keys = useRef(new Map<string, string>());
  const inFlight = useRef(new Set<string>());
  const mutation = useMutation({
    mutationFn: ({ vars, key }: { vars: V; key: string; scope: string }) => send(vars, key),
    onSuccess: (result, { vars, scope }) => {
      keys.current.delete(scope);
      onDone(result, vars);
    },
    onSettled: (_r, _e, { scope }) => {
      inFlight.current.delete(scope);
    },
  });
  const run = (vars: V) => {
    const scope = scopeOf(vars);
    if (inFlight.current.has(scope)) return;
    inFlight.current.add(scope);
    let key = keys.current.get(scope);
    if (!key) {
      key = newKey();
      keys.current.set(scope, key);
    }
    mutation.mutate({ vars, key, scope });
  };
  return {
    run,
    busy: mutation.isPending,
    /** Что сейчас в полёте (для замка конкретной кнопки). */
    pending: mutation.isPending ? (mutation.variables?.vars ?? null) : null,
    data: mutation.data,
    error: mutation.error ? errorText(mutation.error) : "",
    failure: mutation.error,
    reset: mutation.reset,
  };
}

/** ПЛАТНО: один вызов композера → новая версия сценария. */
export function useCompose(pid: number) {
  const client = useQueryClient();
  return usePaid(
    (body: ComposeBody, key) => postJson<ComposeResult>(`/api/projects/${pid}/scenario/compose`, { ...body, key }),
    () => "compose",
    () => refresh(client, pid, true),
  );
}

/**
 * ПЛАТНО (оплачивает воркер): кадры × модели в очередь — всё или ничего. `onSuccess` вызывается в
 * колбэке самой мутации (до перерисовки), чтобы экран успел снять выбор: окна для второго клика по
 * тому же набору с новым ключом нет. Оценки после запуска устарели (потолок, баланс) — сбрасываются.
 */
/**
 * ПЛАТНО: клип ОДНОГО кадра (задача Оли №348 «нужна возможность генерировать отдельно каждый»).
 * Тот же путь и та же ручка, что у общего запуска, но ключ идемпотентности свой на кадр — иначе
 * соседние кнопки делили бы один ключ и второй кадр возвращал бы чужой результат.
 */
export function useRunShotClip(pid: number) {
  const client = useQueryClient();
  return usePaid(
    (params: RunParams, key) => postJson<RunResult>(`/api/projects/${pid}/video/run`, { ...params, key }),
    (params) => `run-shot:${params.shot_ids?.[0] ?? 0}`,
    () => {
      refresh(client, pid, true);
      void client.invalidateQueries({ queryKey: ["video-estimate", pid] });
    },
  );
}

export function useRunClips(pid: number, onSuccess?: (result: RunResult) => void) {
  const client = useQueryClient();
  return usePaid(
    (params: RunParams, key) => postJson<RunResult>(`/api/projects/${pid}/video/run`, { ...params, key }),
    () => "run",
    (result) => {
      onSuccess?.(result);
      refresh(client, pid, true);
      void client.invalidateQueries({ queryKey: ["video-estimate", pid] });
    },
  );
}

/** ПЛАТНО: перезапуск упавшего/отменённого клипа — новая строка с тем же снимком. Ключ — на клип. */
export function useRetryClip(pid: number) {
  const client = useQueryClient();
  return usePaid(
    (gid: number, key) => postJson<{ generation_id: number }>(`/api/clips/${gid}/retry`, { key }),
    (gid) => `retry:${gid}`,
    () => refresh(client, pid, true),
  );
}

/** Состав ракурсов проекта (бесплатно, задача Оли №263): привести кадры к присланным кодам.
 *  Оплаченный ракурс сервер не убирает — 400 `VIEWS_PAID`. */
export function useSetViews(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (angles: string[]) => postJson<ViewsPlanResult>(`/api/projects/${pid}/views/plan`, { angles }),
    onSuccess: () => {
      refresh(client, pid, true);
      void client.invalidateQueries({ queryKey: ["views-estimate", pid] });
    },
  });
}

export type ViewsPlanResult = { added: string[]; removed: string[]; kept: string[] };

export type SourceViewResult = { code: string | null; removed: string[] };

/**
 * Какой ракурс у ИСХОДНОЙ генерации проекта «из кадра» (задача Оли №305, ручка `views/source`,
 * бесплатно): `code` из каталога или `null` — «не указан». Автоподстановки по картинке нет (№307:
 * «пока не делай автоподстановку»), поэтому это единственный способ сказать системе, что средний
 * план (например) у сотрудника уже есть и платить за него не нужно.
 */
export function useSetSourceView(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string | null) => postJson<SourceViewResult>(`/api/projects/${pid}/views/source`, { code }),
    onSuccess: () => {
      refresh(client, pid, true);
      void client.invalidateQueries({ queryKey: ["views-estimate", pid] });
    },
  });
}

/** Оценка пакета ракурсов без записи, бесплатно (§7.3). Запрос только с непустым `model` (решил пользователь). */
export function useViewsEstimate(pid: number, body: ViewsBody, enabled = true) {
  return useQuery({
    queryKey: ["views-estimate", pid, JSON.stringify(body)],
    queryFn: () => postJson<ViewsEstimateResult>(`/api/projects/${pid}/views/estimate`, body),
    enabled: enabled && Number.isFinite(pid),
    retry: false,
  });
}

/** ПЛАТНО (оплачивает воркер): пакет ракурсов в очередь — тот же `batch_key`, что и повтор, не платит дважды. */
export function useRunViews(pid: number) {
  const client = useQueryClient();
  return usePaid(
    (body: ViewsRunBody, key) => postJson<ViewsRunResult>(`/api/projects/${pid}/views`, { ...body, key }),
    () => "views",
    () => {
      refresh(client, pid, true);
      void client.invalidateQueries({ queryKey: ["views-estimate", pid] });
    },
  );
}

/** ПЛАТНО: промпт клипа по картинке кадра (§8) — пишет в пустые поля, занятые не трогает. Ключ — на кадр. */
export function useClipPrompt(pid: number) {
  const client = useQueryClient();
  return usePaid(
    (sid: number, key) => postJson<ClipPromptResult>(`/api/shots/${sid}/clip-prompt`, { key }),
    (sid) => `clip-prompt:${sid}`,
    () => {
      refresh(client, pid);
      // Записанный промпт снимает блокер запуска — оценка живёт по своему ключу и сама не обновится
      // (найдено ревью 16.10): без этого платный ответ есть, а «Запустить» всё ещё выключена.
      void client.invalidateQueries({ queryKey: ["video-estimate", pid] });
    },
  );
}

/**
 * Монтаж: денег не стоит (ffmpeg у нас), повтор держит отпечаток набора, но ключ шлём по тому же
 * правилу — для единообразия с сервером.
 */
export function useCut(pid: number) {
  const client = useQueryClient();
  return usePaid(
    (body: CutBody, key) => postJson<CutResult>(`/api/projects/${pid}/video/cuts`, { ...body, key }),
    () => "cut",
    () => refresh(client, pid),
  );
}

/**
 * Оценка «Финала в 1080p» монтажа со звездой (бесплатно, без записи). 400 `NO_HD` — у моделей клипов
 * нет формата больше («у модели нет 1080p»): кнопка выключена с этой причиной.
 */
export function useFinalHdEstimate(cutId: number, enabled: boolean) {
  return useQuery({
    queryKey: videoKeys.finalHd(cutId),
    queryFn: () => postJson<FinalHdResult>(`/api/cuts/${cutId}/final-hd/estimate`, {}),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

/** ПЛАТНО (воркер): клипы кадров монтажа со звездой заново в 1080p. Ключ — на монтаж, до успеха. */
export function useFinalHd(pid: number) {
  const client = useQueryClient();
  return usePaid(
    (cutId: number, key) => postJson<FinalHdResult>(`/api/cuts/${cutId}/final-hd`, { key }),
    (cutId) => `final:${cutId}`,
    (_r, cutId) => {
      refresh(client, pid, true);
      void client.invalidateQueries({ queryKey: videoKeys.finalHd(cutId) });
    },
  );
}

// ---------------------------------------------------------------------------
// бесплатные записи
// ---------------------------------------------------------------------------

/** Платье, стилевая библиотека, цель ролика, потолок трат; ответ — новый `video`. 409 `HAS_CLIPS` — смена платья. */
export function useVideoSettings(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SettingsBody) => postJson<VideoView>(`/api/projects/${pid}/video/settings`, body),
    onSuccess: () => {
      refresh(client, pid);
      // в оценке запуска — «осталось до потолка» и блокер потолка
      void client.invalidateQueries({ queryKey: ["video-estimate", pid] });
    },
  });
}

/** Фото платья в слоты проекта заново (+ сцена в `scene.main`). */
export function useFill(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: FillBody) => postJson<FillResult>(`/api/projects/${pid}/video/fill`, body),
    onSuccess: () => refresh(client, pid),
  });
}

/** Версия сценария руками — форма v2 (`toV2`); 422 `BAD_SCENARIO` — причина текстом. */
export function useManualScenario(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (json: ScenarioDraftV2) =>
      postJson<{ scenario: ScenarioVersion }>(`/api/projects/${pid}/scenario/manual`, { json }),
    onSuccess: () => refresh(client, pid),
  });
}

/**
 * «Применить»: `shots` — кадры по сценарию, `fields` — только поля ролика. 409
 * `SHOTS_HAVE_GENERATIONS` — у кадров есть генерации: `cloneLink(error)` и диалог «создать копию».
 */
export function useApplyScenario(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, mode }: { scenarioId: number; mode: ApplyMode }) =>
      postJson<ApplyResult>(`/api/projects/${pid}/scenario/${scenarioId}/apply`, { mode }),
    onSuccess: () => refresh(client, pid),
  });
}

/** Проект-копия (без кадров, генераций и версий) → `{id}`; переход делает вызывающий. */
export function useClone(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => postJson<{ id: number }>(`/api/projects/${pid}/clone`, {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.projects }),
  });
}

/**
 * Ответ на 409 `SHOTS_HAVE_GENERATIONS`: проект-копия, в ней — та же версия сценария (версии не
 * копируются, поэтому — ручной версией из того же JSON в форме v2, без нового вызова композера) и применение к
 * кадрам. Копия пуста — кадры создаются по сценарию. Не удалось применить в копии — `{id, error}`:
 * копия уже есть, туда и переходим, сценарий доделают там.
 */
export function useCloneAndApply(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (json: ScenarioDraftV2): Promise<{ id: number; error: string }> => {
      const { id } = await postJson<{ id: number }>(`/api/projects/${pid}/clone`, {});
      try {
        const { scenario } = await postJson<{ scenario: ScenarioVersion }>(`/api/projects/${id}/scenario/manual`, {
          json,
        });
        await postJson<ApplyResult>(`/api/projects/${id}/scenario/${scenario.id}/apply`, { mode: "shots" });
        return { id, error: "" };
      } catch (e) {
        return { id, error: errorText(e) };
      }
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.projects }),
  });
}

/** Порядок кадров (существующая ручка раскадровки) — для перетаскивания по хронологии. */
export function useShotOrder(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (order: number[]) => postJson<{ moved: number }>(`/api/projects/${pid}/shots/order`, { order }),
    onSettled: () => refresh(client, pid),
  });
}

/** Отмена клипа в очереди; отправленный — 409 `RUNNING` (уже оплачен). */
export function useCancelClip(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (gid: number) => postJson<{ status: ClipStatus }>(`/api/clips/${gid}/cancel`, {}),
    onSuccess: () => refresh(client, pid),
  });
}

/**
 * Пересборка монтажа с теми же клипами, переходом, титром и музыкой. Если с прошлого раза ничего не
 * изменилось, сервер вернёт ПРЕЖНИЙ монтаж (`existing`), а не заведёт дубль (№361).
 */
export function useRebuildCut(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (cutId: number) => postJson<{ cut: Cut; existing: boolean }>(`/api/cuts/${cutId}/rebuild`, {}),
    onSuccess: () => refresh(client, pid),
  });
}

/** Удалить ненужный монтаж (№361): бесплатно; взятый в «Результаты» или с оплаченным финалом — 409. */
export function useDeleteCut(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (cutId: number) => apiFetch<{ deleted: number }>(`/api/cuts/${cutId}`, { method: "DELETE" }),
    onSuccess: () => refresh(client, pid),
  });
}

/** Звезда монтажу: «лучший» в проекте один. */
export function usePickCut(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (cutId: number) => postJson<{ cut: Cut }>(`/api/cuts/${cutId}/pick`, {}),
    onSuccess: () => refresh(client, pid),
  });
}

/** «В результаты»: готовый монтаж → вариант «Результатов» (бесплатно, идемпотентно, `videocut.adopt`);
 * повтор на уже взятом монтаже отдаёт тот же `variant_id`. */
export function useAdoptCut(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (cutId: number) =>
      postJson<{ cut: Cut; generation_id: number; variant_id: number }>(`/api/cuts/${cutId}/adopt`, {}),
    onSuccess: () => refresh(client, pid),
  });
}

/** Музыка монтажа: mp3/m4a → `{asset_id, path}` (400 `BAD_AUDIO`). */
export function useUploadMusic(pid: number) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetch<{ asset_id: number; path: string }>(`/api/projects/${pid}/video/music`, {
        method: "POST",
        body: form,
      });
    },
  });
}

/** Знак монтажа (wordmark): PNG с прозрачностью ≤ 2 МБ → `{asset_id, path}` (400 `BAD_LOGO`). */
export function useUploadLogo(pid: number) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetch<{ asset_id: number; path: string }>(`/api/projects/${pid}/video/logo`, {
        method: "POST",
        body: form,
      });
    },
  });
}

/** Стилл кадра — смена картинки без участия генераций (§5/§10): `variantId: null` снимает ссылку. Бесплатно. */
export function useSetStill(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sid, variantId }: { sid: number; variantId: number | null }) =>
      postJson<StillResult>(`/api/shots/${sid}/still`, { variant_id: variantId } as StillBody),
    onSuccess: () => refresh(client, pid),
  });
}

/** Поле ролика у кадра (существующая ручка `shot_field`): длительность — мс, кратно 500. */
export function useShotField(pid: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sid, field, value }: { sid: number; field: ShotVideoField; value: string | number }) =>
      postJson<FieldResult>(`/api/projects/${pid}/shots/${sid}/field`, { field, value: String(value) }),
    onSuccess: () => refresh(client, pid),
  });
}

/** Новый видео-проект из платья → `{id}` (400 `NO_DRESS`, `NO_PHOTOS`, `BAD_DURATION`, `NO_LIBRARY`). */
export function useCreateVideoProject() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVideoBody) => postJson<{ id: number }>("/api/projects", body),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.projects }),
  });
}
